import { Classes, Intent } from "@blueprintjs/core";
import { OrgRole, isOrgRoleSuperset } from "@hex/common";
import React, { useCallback, useEffect } from "react";
import styled, { css } from "styled-components";

import { HexButton } from "../../../../hex-components/HexButton.js";
import {
  HexMenu,
  HexMenuDivider,
  HexRadio,
  Txt,
} from "../../../../hex-components/index.js";
import { useCurrentUser } from "../../../../hooks/me/useCurrentUser.js";
import { MinimalStatus } from "../../../../hooks/useProjectLabelsForHex.js";
import { useSelector } from "../../../../redux/hooks.js";
import { hexSelectionSelectors } from "../../../../redux/slices/hexSelectionSlice.js";
import { EndorsedStatusRow } from "../../../app/ProjectLabels.js";
import {
  StatusLabel,
  StatusLabelProps,
} from "../../../common/labels/StatusLabel.js";
import { DisallowIcon } from "../../../icons/CustomIcons.js";

export interface StatusPickerPopoverProps {
  newStatus: MinimalStatus | null | undefined;
  statuses: readonly (MinimalStatus & { enforcesReview: boolean })[];
  handleCancel: () => void;
  handleSave: () => void;
  setNewStatus: (status: MinimalStatus | null | undefined) => void;
  /** When provided, uses this as the initial status instead of reading from redux bulk selection */
  initialStatus?: MinimalStatus | null;
  /** Whether the save action is loading */
  loading?: boolean;
}

const descriptionTooltipProps: StatusLabelProps["descriptionTooltipProps"] = {
  placement: "right",
};

export const StatusPickerPopover: React.ComponentType<StatusPickerPopoverProps> =
  React.memo(function StatusPickerPopover({
    handleCancel,
    handleSave,
    initialStatus,
    loading,
    newStatus,
    setNewStatus,
    statuses,
  }) {
    const currentUser = useCurrentUser();
    const isManagerOrAbove =
      currentUser?.orgRole != null &&
      isOrgRoleSuperset(currentUser.orgRole, OrgRole.MANAGER);

    const selectedStatusIds = useSelector(
      hexSelectionSelectors.selectSelectedStatusIdsSet,
    );

    // Initialize status from redux bulk selection or from initialStatus prop
    useEffect(() => {
      // If initialStatus is explicitly provided (even if null), use it
      if (initialStatus !== undefined) {
        setNewStatus(initialStatus);
      } else if (selectedStatusIds.size === 1) {
        const statusId = selectedStatusIds.values().next().value;
        const status = statuses.find((s) => s.id === statusId);
        setNewStatus(status ?? null);
      } else {
        setNewStatus(undefined);
      }
    }, [initialStatus, selectedStatusIds, setNewStatus, statuses]);

    const clearStatus = useCallback(() => {
      setNewStatus(null);
    }, [setNewStatus]);

    const { basicStatuses, endorsedStatuses } = statuses.reduce(
      (
        acc: {
          basicStatuses: MinimalStatus[];
          endorsedStatuses: (MinimalStatus & {
            enforcesReview: boolean;
          })[];
        },
        status,
      ) => {
        if (status.endorsed) {
          acc.endorsedStatuses.push(status);
        } else {
          acc.basicStatuses.push(status);
        }
        return acc;
      },
      { basicStatuses: [], endorsedStatuses: [] },
    );

    return (
      <StatusPicker>
        <StatusList>
          {endorsedStatuses.length > 0 &&
            endorsedStatuses.map((status) => (
              <StyledHexRadio
                key={status.id}
                checked={newStatus?.id === status.id}
                disabled={!isManagerOrAbove}
                labelElement={
                  <EndorsedStatusRow resourceType="hex" status={status} />
                }
                value={status.id}
                // eslint-disable-next-line react/jsx-no-bind -- in a loop
                onChange={() => setNewStatus(status)}
              />
            ))}
          {basicStatuses.map((status) => (
            <StyledHexRadio
              key={status.id}
              checked={newStatus?.id === status.id}
              labelElement={
                <StatusLabel
                  descriptionTooltipProps={descriptionTooltipProps}
                  status={status}
                />
              }
              value={status.id}
              // eslint-disable-next-line react/jsx-no-bind -- in a loop
              onChange={() => setNewStatus(status)}
            />
          ))}
          <StyledHexRadio
            // eslint-disable-next-line eqeqeq -- intentional distinction between null and undefined
            checked={newStatus === null}
            labelElement={
              <NoStatusRow fontColor="muted" fontSize="small">
                <DisallowIcon /> No status
              </NoStatusRow>
            }
            onChange={clearStatus}
          />
        </StatusList>
        <HexMenuDivider />
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 2px;
            margin: 2px 5px;
          `}
        >
          <HexButton
            className={Classes.POPOVER_DISMISS}
            disabled={loading}
            minimal={true}
            small={true}
            onClick={handleCancel}
          >
            Cancel
          </HexButton>
          <HexButton
            className={Classes.POPOVER_DISMISS}
            disabled={loading}
            intent={Intent.PRIMARY}
            loading={loading}
            small={true}
            subtle={true}
            onClick={handleSave}
          >
            Save
          </HexButton>
        </div>
      </StatusPicker>
    );
  });

const StatusPicker = styled(HexMenu)`
  display: flex;
  flex-direction: column;
  max-height: 500px;

  && {
    min-width: 150px;
  }
`;

const StatusList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: calc(100% - 40px);
  gap: 4px;
  overflow-y: auto;
  padding: 8px;

  /* Ensure radio indicators are vertically centered with their labels */
  .${Classes.CONTROL} {
    display: flex;
    align-items: center;
  }

  .${Classes.CONTROL_INDICATOR} {
    margin-top: 0 !important;
  }
`;

const StyledHexRadio = styled(HexRadio)`
  height: 24px;
`;

const NoStatusRow = styled(Txt)`
  display: flex;
  align-items: center;
  gap: 2px;
  padding-left: 4px;

  svg {
    width: 14px;
    height: 14px;
  }
`;
